<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDropdown } from '@/composables/useDropdown.js'
import { getStateName } from '@/libs/utils/workflows.js'
import { getPollTemplate, getPollTemplateHistory, getPollTemplateHistoryByIdAndRevision } from '@/services/pollTemplateService.js'
import { getWorkflow } from '@/services/workflowService.js'

import QuestionGroupStatistics from '@/components/PollTemplate/Components/QuestionGroupStatistics.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faRotate, faChevronLeft, faXmark, faArrowLeft } from '@fortawesome/free-solid-svg-icons'

library.add(faRotate)
library.add(faChevronLeft)
library.add(faXmark)
library.add(faArrowLeft)

const { dropDownToggleF, hideDropdowns } = useDropdown()
const props = defineProps(['updatePageTitle'])
const route = useRoute()
const router = useRouter()

const canView = ref(false)
const loading = ref(true)
const statistics = ref(null)
const workflows = ref([])
const templateHistory = ref([])
const selectedPollTemplate = ref(null)

onMounted(async () => {
  canView.value = route.meta.permissions.canView ?? false
  if (!canView.value) {
    return
  }

  loading.value = true

  await getStats()
  await getStatisticsByRevision(selectedPollTemplate.value.revisionNumber)

  loading.value = false

  props.updatePageTitle('Statistiche template - ' + (selectedPollTemplate.value?.title ?? ''))
})

async function getStats() {
  selectedPollTemplate.value = await getPollTemplate(route.params.id, { complete: 1, stats: 1 })
  templateHistory.value = await getPollTemplateHistory(route.params.id, {perPage: 50})
}

async function getStatisticsByRevision(revisionNumber) {
  loading.value = true
  statistics.value = null
  workflows.value = []

  try {
    if (revisionNumber === selectedPollTemplate.value.revisionNumber) {
      statistics.value = selectedPollTemplate.value
    } else {
      statistics.value = await getPollTemplateHistoryByIdAndRevision(
        selectedPollTemplate.value.id,
        revisionNumber,
        {complete: 1, stats: 1}
      )
    }

    if (statistics.value.placesNumber && Object.values(statistics.value.placesNumber).length) {
      for (const workflow of statistics.value.placesNumber.workflow) {
        const response = await getWorkflow(workflow.key)
        workflows.value.push(response.data)
      }
    }

    loading.value = false
  } catch (error) {
    toast.create('Errore durante il recupero dei dati, riprova', 'error')
    loading.value = false
  }
}

</script>

<template>
  <div class="container-fluid compiled-polls-list" @click="hideDropdowns">
    <div class="row">
      <div class="col-12 px-0">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-end">

              <div class="dropdown me-2">
                <button class="btn btn-secondary btn-sm dropdown-toggle dropdown-toggle-b"
                        :disabled="loading"
                        type="button"
                        data-toggle="dropdown"
                        aria-expanded="false"
                        ref="dropdown-toggle"
                        @click="dropDownToggleF"
                >
                  Revisione
                </button>
                <ul class="dropdown-menu revision-dropdown">
                  <li v-for="template in templateHistory.data" :key="template.revisionNumber">
                    <span class="dropdown-item small cursor-pointer"
                          :class="{'disabled': template.revisionNumber === statistics?.revisionNumber}"
                          @click="getStatisticsByRevision(template.revisionNumber)"
                    >
                      {{ template.revisionNumber }}

                       <span v-if="template.revisionNumber === statistics?.revisionNumber"
                             class="badge rounded-pill bg-light ms-2 text-gray"
                       >
                        Selezionato
                       </span>
                    </span>
                  </li>
                </ul>
              </div>

              <button class="btn btn-primary btn-sm spin"
                      :disabled="loading"
                      @click="getStatisticsByRevision(statistics.revisionNumber)">
                <font-awesome-icon :spin="loading" icon="fa-solid fa-rotate" />
              </button>
            </div>
          </div>
          <div class="card-body">

            <loading v-if="loading"></loading>

            <div class="col-12 px-0" v-else-if="!loading">
              <div v-if="statistics?.placesNumber?.workflow
                       && statistics.placesNumber.workflow.length > 0
                       && statistics.placesNumber.workflow[0]?.places"
              >
                <h5>Compilazioni:</h5>
                <ul>
                  <li v-for="(num, state) in statistics.placesNumber.workflow[0].places" :key="state">
                    {{ getStateName(state, workflows) }}: {{ num }}
                  </li>
                </ul>
              </div>

              <h6>Domande</h6>
              <question-group-statistics
                v-if="statistics"
                v-for="(questionGroup) in statistics['questionGroups']"
                :key="questionGroup.id"
                :statistics="statistics"
                :workflows="workflows"
                :question-group="questionGroup">
              </question-group-statistics>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div class="d-flex justify-content-between mt-3">
      <button-torna-alla-lista @click="router.back()"></button-torna-alla-lista>
    </div>

  </div>
</template>

<style>
.revision-dropdown {
  max-height: 200px;
  overflow-y: scroll;
}
</style>
